import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Modal, ConfirmModal } from '@/components/ui/Modal';
import {
  getStaff,
  addStaff,
  removeStaff,
  updateStaffRole,
  type StaffMember,
  type StaffRole,
} from '@/api/staff';
import { ApiError } from '@/api/client';
import { Users, UserCheck, UserX, Plus, Trash2, Pencil } from 'lucide-react';

const roleLabels: Record<StaffRole, string> = {
  owner: '老板',
  frontdesk: '前台',
  technician: '师傅',
  finance: '财务',
};

const roleBadgeVariants: Record<StaffRole, 'default' | 'primary' | 'success' | 'warning'> = {
  owner: 'primary',
  frontdesk: 'success',
  technician: 'warning',
  finance: 'default',
};

const editableRoles: StaffRole[] = ['frontdesk', 'technician', 'finance'];

export function StaffPage() {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ phone: '', displayName: '', role: 'technician' as StaffRole });
  const [editingRole, setEditingRole] = useState<StaffMember | null>(null);
  const [newRole, setNewRole] = useState<StaffRole>('technician');
  const [removing, setRemoving] = useState<StaffMember | null>(null);
  const [error, setError] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['staff'],
    queryFn: getStaff,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['staff'] });

  const addMutation = useMutation({
    mutationFn: () =>
      addStaff({
        phone: addForm.phone.trim(),
        role: addForm.role,
        displayName: addForm.displayName.trim() || undefined,
      }),
    onSuccess: () => {
      setAddOpen(false);
      setAddForm({ phone: '', displayName: '', role: 'technician' });
      setError('');
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '添加失败'),
  });

  const roleMutation = useMutation({
    mutationFn: () => updateStaffRole(editingRole!.userId, newRole),
    onSuccess: () => {
      setEditingRole(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '修改失败'),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeStaff(userId),
    onSuccess: () => {
      setRemoving(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '移除失败'),
  });

  const boundCount = data?.items.filter((s) => s.bound).length ?? 0;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1E293B]">员工权限</h1>
          <p className="mt-1 text-sm text-[#64748B]">
            管理门店员工账号与角色权限
          </p>
        </div>
        <Button onClick={() => { setError(''); setAddOpen(true); }}>
          <Plus className="mr-1 h-4 w-4" />
          添加员工
        </Button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]">{error}</div>
      )}

      {/* 统计 */}
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#64748B]" />
            <span className="text-sm text-[#64748B]">
              共 <span className="font-medium text-[#1E293B]">{data?.total ?? 0}</span> 名员工
            </span>
          </div>
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-[#16A34A]" />
            <span className="text-sm text-[#64748B]">
              已绑定 <span className="font-medium text-[#16A34A]">{boundCount}</span> 人
            </span>
          </div>
          <div className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-[#94A3B8]" />
            <span className="text-sm text-[#64748B]">
              待绑定 <span className="font-medium text-[#64748B]">{(data?.total ?? 0) - boundCount}</span> 人
            </span>
          </div>
        </CardContent>
      </Card>

      {/* 员工列表 */}
      <Card>
        <CardHeader>
          <CardTitle>员工列表</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-sm text-[#94A3B8]">加载中...</div>
          ) : isError ? (
            <div className="py-12 text-center text-sm text-[#DC2626]">加载失败，请刷新重试</div>
          ) : !data?.items.length ? (
            <div className="py-12 text-center">
              <Users className="mx-auto h-12 w-12 text-[#E2E8F0]" />
              <p className="mt-3 text-sm text-[#94A3B8]">
                暂无员工，点击右上角"添加员工"
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left text-sm text-[#64748B]">
                    <th className="pb-3 font-medium">姓名</th>
                    <th className="pb-3 font-medium">手机号</th>
                    <th className="pb-3 font-medium">角色</th>
                    <th className="pb-3 font-medium">绑定状态</th>
                    <th className="pb-3 font-medium">添加时间</th>
                    <th className="pb-3 font-medium text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {data.items.map((staff) => (
                    <tr key={staff.userId} className="text-sm">
                      <td className="py-3">
                        <div className="font-medium text-[#1E293B]">
                          {staff.displayName || '未设置'}
                        </div>
                      </td>
                      <td className="py-3 text-[#64748B]">{staff.phone}</td>
                      <td className="py-3">
                        <Badge variant={roleBadgeVariants[staff.role]}>
                          {roleLabels[staff.role]}
                        </Badge>
                      </td>
                      <td className="py-3">
                        {staff.bound ? (
                          <Badge variant="success">已绑定</Badge>
                        ) : (
                          <Badge variant="default">待绑定</Badge>
                        )}
                      </td>
                      <td className="py-3 text-[#94A3B8]">
                        {new Date(staff.createdAt).toLocaleDateString('zh-CN')}
                      </td>
                      <td className="py-3">
                        {staff.role !== 'owner' && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              title="修改角色"
                              onClick={() => {
                                setEditingRole(staff);
                                setNewRole(staff.role);
                              }}
                              className="rounded-lg p-1.5 text-[#2563EB] hover:bg-[#EFF6FF]"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              title="移除"
                              onClick={() => setRemoving(staff)}
                              className="rounded-lg p-1.5 text-[#DC2626] hover:bg-[#FEF2F2]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 添加员工弹窗 */}
      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="添加员工"
        footer={
          <>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addMutation.isPending}>
              取消
            </Button>
            <Button
              onClick={() => addMutation.mutate()}
              disabled={addMutation.isPending || !addForm.phone.trim()}
            >
              {addMutation.isPending ? '添加中...' : '添加'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">手机号 *</label>
            <Input
              value={addForm.phone}
              onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
              placeholder="员工手机号"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">姓名</label>
            <Input
              value={addForm.displayName}
              onChange={(e) => setAddForm({ ...addForm, displayName: e.target.value })}
              placeholder="员工姓名（可选）"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">角色 *</label>
            <Select
              value={addForm.role}
              onChange={(e) => setAddForm({ ...addForm, role: e.target.value as StaffRole })}
            >
              {editableRoles.map((r) => (
                <option key={r} value={r}>{roleLabels[r]}</option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>

      {/* 修改角色弹窗 */}
      <Modal
        open={!!editingRole}
        onClose={() => setEditingRole(null)}
        title={`修改角色 — ${editingRole?.displayName ?? editingRole?.phone ?? ''}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setEditingRole(null)} disabled={roleMutation.isPending}>
              取消
            </Button>
            <Button onClick={() => roleMutation.mutate()} disabled={roleMutation.isPending}>
              {roleMutation.isPending ? '保存中...' : '保存'}
            </Button>
          </>
        }
      >
        <div>
          <label className="mb-1 block text-sm font-medium text-[#334155]">新角色</label>
          <Select value={newRole} onChange={(e) => setNewRole(e.target.value as StaffRole)}>
            {editableRoles.map((r) => (
              <option key={r} value={r}>{roleLabels[r]}</option>
            ))}
          </Select>
        </div>
      </Modal>

      {/* 移除确认 */}
      <ConfirmModal
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && removeMutation.mutate(removing.userId)}
        title="移除员工"
        description={`确定将「${removing?.displayName || removing?.phone}」从门店移除吗？`}
        confirmText="移除"
        danger
        loading={removeMutation.isPending}
      />
    </div>
  );
}
